import { AppMode } from 'app/store/general/AppMode';
import Services from 'store/Services';
import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { getWindowLocation } from 'utils/components/location';
import { getGlobalWindowVariables } from 'utils/globalWindowVariables';
import { useDispatchSetAppMode } from 'app/store/general/dispatcher';
import { useDispatchPortfolioSetActiveInput } from 'portfolio/store/dispatcher';
import { useDispatchSetLayoutPanelId } from 'layout/store/dispatcher';
import { useLoginStateSelector } from 'login/store/selectors';
import { AppContextProvider } from 'app/AppContext';
import EmbeddedLoginError from 'embedded/Login/EmbeddedLoginError';
import {
    buildEmbeddedAppLoginErrorTextFromLoginStatus,
    buildEmbeddedLoginErrorTextFromWebsocketErrorCode,
} from 'embedded/Login/EmbeddedLoginErrorText';
import {
    useAvailabilityStatus,
    useNetworkConnectivityStatus,
} from 'app/store/connectivity/selectors';
import { useReloadRequired } from 'embedded/api/hooks/useReloadRequired';
import EmbeddedLoginErrorWithErrorCode from 'embedded/Login/EmbeddedLoginErrorWithErrorCode';
import { LoginStatus } from 'login/store/LoginState';
import Layout from 'layout/Layout';
import './EmbeddedApp.scss';
import { v4 as uuid } from 'uuid';
import { buildLogger, ForceLoggingWrapper } from '@instinet-analytics/core-lib';
import { WellKnownLayoutOrigin } from 'layout/WellKnownLayoutOrigin';
import { loginJwtAction } from 'login/store/thunks';
import { useDispatch } from 'store/hooks';
import { GlueSupport } from 'app/glue/GlueSupport';
import { ReauthenticationRequiredMessage } from 'embedded/api/out/messages/ReauthenticationRequiredMessage';
import NetworkState from 'communication/NetworkState';
import { OpenFinSupport } from '../app/openFin/OpenFinSupport';
import { availabilyStatusDesc } from 'communication/streaming/StreamStatusMessage';
import EmbeddedLoginAuthenticating from './Login/EmbeddedLoginAuthenticating';
import { VersionWarnings } from 'app/VersionWarnings/VersionWarnings';
import { using as usingStatement } from 'using-statement';
import { EmbeddedAppStylesheet } from 'embedded/EmbeddedAppStylesheet.tsx';

export interface EmbeddedAppProps {
    layout: string;
    mode: AppMode.EMBEDDED_SINGLE_WIDGET | AppMode.EMBEDDED_DASHBOARD;
    services: Services;
    /*
     * Set when Newport is using a login key, to avoid showing waiting message as they will never handle the REAUTHENTICATION_REQUIRED message
     */
    suppressRequestedHostReauthenticates?: boolean;
    onReconnected?: () => { handled: boolean };
    onInitialiseStore?: (panelId: string, mode: AppMode) => void;
}

/*
 * An Indigo App embedded in another web app. All input will be via the Embedded API.
 * Authentication will be via a JWT token received in the Embedded Api
 * Based on a Newport App (without updateParms support)
 */
export default function EmbeddedApp(props: EmbeddedAppProps): ReactElement {
    const {
        mode,
        services,
        layout,
        suppressRequestedHostReauthenticates,
        onReconnected: onReconnectedProp,
        onInitialiseStore: onInitialiseStoreProp,
    } = props;
    const { embeddedApiService, loggingService, indigoApi } = services;

    const logger = useMemo(
        () => buildLogger('EmbeddedApp', loggingService),
        [loggingService]
    );

    const loginState = useLoginStateSelector();
    const { status: loginStatus, error: loginError } = loginState;

    const dispatchSetAppMode = useDispatchSetAppMode();
    const dispatchSetActiveInput = useDispatchPortfolioSetActiveInput();
    const dispatchLayoutSetPanelId = useDispatchSetLayoutPanelId();
    const dispatch = useDispatch();

    const networkConnectivityStatus = useNetworkConnectivityStatus();
    const lostNetworkConnection =
        networkConnectivityStatus.lostConnectionStatus;

    const origin: WellKnownLayoutOrigin =
        mode === AppMode.EMBEDDED_SINGLE_WIDGET
            ? WellKnownLayoutOrigin.WIDGETS
            : WellKnownLayoutOrigin.PALETTE;

    const urlParams = new URLSearchParams(getWindowLocation().search);
    const globals = getGlobalWindowVariables();
    const panelId = globals.panelId || urlParams.get('PanelId')!;
    const { errorCode } = globals;
    const { reloadPossible } = useReloadRequired();

    const [requestedHostReauthenticates, setRequestedHostReauthenticates] =
        useState(false);

    const initialiseStore = useCallback(() => {
        logger.debug('Initialising store for Embedded App');
        dispatchSetAppMode(mode);
        dispatchSetActiveInput('Embedded App');
        if (panelId) {
            dispatchLayoutSetPanelId(panelId);
        }
        if (onInitialiseStoreProp) {
            onInitialiseStoreProp(panelId, mode);
        }
    }, [
        logger,
        mode,
        dispatchSetAppMode,
        dispatchSetActiveInput,
        panelId,
        dispatchLayoutSetPanelId,
        onInitialiseStoreProp,
    ]);

    const dispatchJwtLogin = useCallback(() => {
        if (globals.hasAllJwtAuthenticationDetails) {
            logger.debug('Dispatching JWT login');

            const payload = {
                token: globals.jwt!,
                application: globals.application!,
                applicationVersion: globals.applicationVersion,
                machineId: globals.machineId,
                correlationid: undefined,
            };
            usingStatement(new ForceLoggingWrapper(loggingService), () => {
                logger.info(`Dispatching JWT login from globals`, { payload });
            });

            dispatch(loginJwtAction(payload));
        }
    }, [
        globals.hasAllJwtAuthenticationDetails,
        globals.jwt,
        globals.application,
        globals.applicationVersion,
        globals.machineId,
        dispatch,
        logger,
        loggingService,
    ]);

    useEffect(() => {
        initialiseStore();
        dispatchJwtLogin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatchJwtLogin]);

    const sendReauthenticationRequired = useCallback(() => {
        if (suppressRequestedHostReauthenticates) {
            logger.info(
                'Login error after reconnection: host does not support REAUTHENTICATION_REQUIRED'
            );
            return;
        }
        logger.info(
            'Login error after reconnection: asking host to reauthenticate'
        );
        const message: ReauthenticationRequiredMessage = {
            topic: 'REAUTHENTICATION_REQUIRED',
            payload: {
                correlationId: uuid(),
            },
        };
        setRequestedHostReauthenticates(true);
        embeddedApiService.send(message);
    }, [embeddedApiService, logger, suppressRequestedHostReauthenticates]);

    useEffect(() => {
        logger.debug('Login status changed', {
            loginStatus,
            requestedHostReauthenticates,
        });

        if (loginStatus === LoginStatus.SUCCESS) {
            initialiseStore();
            setRequestedHostReauthenticates(false);
        }

        if (loginStatus === LoginStatus.ERROR) {
            if (requestedHostReauthenticates) {
                // This was after a request to the host for a new JWT token - stop here...
                setRequestedHostReauthenticates(false);
            } else {
                // First attempt with this JWT token => ask host to reauthenticate
                sendReauthenticationRequired();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginStatus]);

    const onReconnectedToIndigo = useCallback(() => {
        initialiseStore();
        if (onReconnectedProp) {
            // Parent component (NewportApp) handles the reconnect...
            const { handled } = onReconnectedProp();
            if (handled) {
                return;
            }
        }

        if (globals.hasAllJwtAuthenticationDetails) {
            logger.debug('Attempting JWT login on reconnect');
            // Try logon with the same details../
            dispatchJwtLogin();
        } else {
            // Ask host to reauthenticate...
            logger.debug('Sending reauthentication required after reconnect');
            sendReauthenticationRequired();
        }
    }, [
        globals.hasAllJwtAuthenticationDetails,
        onReconnectedProp,
        initialiseStore,
        logger,
        dispatchJwtLogin,
        sendReauthenticationRequired,
    ]);

    useEffect(() => {
        indigoApi.registerStreamingServiceReconnectedToIndigoCallback(
            onReconnectedToIndigo
        );
    }, [indigoApi, onReconnectedToIndigo]);

    const availabilityStatus = useAvailabilityStatus();
    const indigoUnavailable =
        availabilityStatus && !availabilityStatus?.available;

    if (indigoUnavailable) {
        return (
            <>
                <EmbeddedAppStylesheet />
                <EmbeddedLoginError
                    text={{
                        title: `Indigo backend is unavailable (${availabilyStatusDesc(
                            availabilityStatus?.status
                        )})`,
                        instructions: '',
                    }}
                    hideReload={true}
                />
            </>
        );
    }

    if (errorCode) {
        return (
            <AppContextProvider services={services}>
                <EmbeddedAppStylesheet />
                <EmbeddedLoginErrorWithErrorCode
                    prefix={
                        <VersionWarnings
                            clientWarnings={true}
                            showRecommended={true}
                        />
                    }
                />
            </AppContextProvider>
        );
    }

    if (
        (globals.jwt || globals.applicationVersion) &&
        !globals.hasAllJwtAuthenticationDetails
    ) {
        return (
            <AppContextProvider services={services}>
                <EmbeddedAppStylesheet />
                <EmbeddedLoginError
                    text={{
                        title: 'Authentication failure.',
                        instructions:
                            'Incomplete authentication details received',
                    }}
                />
            </AppContextProvider>
        );
    }

    let notAuthenticatedMessage: string | undefined = undefined;

    if (requestedHostReauthenticates) {
        notAuthenticatedMessage = 'Waiting for login from host';
    } else if (loginStatus === LoginStatus.PENDING) {
        notAuthenticatedMessage = 'Authenticating';
    } else if (loginStatus === LoginStatus.NONE) {
        if (globals.hasAllJwtAuthenticationDetails) {
            notAuthenticatedMessage = 'Authenticating';
        } else {
            notAuthenticatedMessage = 'Waiting for login from host';
        }
    }

    return (
        <AppContextProvider services={services}>
            <GlueSupport />
            <OpenFinSupport />
            <EmbeddedAppStylesheet />
            {lostNetworkConnection && (
                <EmbeddedLoginError
                    text={buildEmbeddedLoginErrorTextFromWebsocketErrorCode(
                        (lostNetworkConnection as NetworkState).errorCode,
                        reloadPossible
                    )}
                />
            )}
            {!lostNetworkConnection && (
                <>
                    {notAuthenticatedMessage && (
                        <EmbeddedLoginAuthenticating
                            message={notAuthenticatedMessage}
                            prefix={
                                <VersionWarnings
                                    clientWarnings={true}
                                    showRecommended={true}
                                />
                            }
                        />
                    )}
                    {!notAuthenticatedMessage &&
                        loginStatus === LoginStatus.ERROR && (
                            <EmbeddedLoginError
                                text={buildEmbeddedAppLoginErrorTextFromLoginStatus(
                                    loginError?.innerError?.status ?? 500,
                                    reloadPossible
                                )}
                            />
                        )}
                    {!notAuthenticatedMessage &&
                        loginStatus === LoginStatus.SUCCESS && (
                            <Layout origin={origin} layout={layout} />
                        )}
                </>
            )}
        </AppContextProvider>
    );
}
